// Customer registration flow: email -> verify -> details+password -> auto-
// login -> /account. Same step-machine shape as pages/GuestPage.jsx (see
// that file's own header comment for the full reasoning — one stateful page
// owning a linear flow, not five routes), reusing GuestEmailStep/
// GuestVerifyStep with idPrefix='register*' for registration-specific copy
// (portal/CLAUDE.md's 120KB budget makes that reuse worth it over two
// near-duplicate components).
//
// Resilience: email/verifiedAt/details are persisted the same way
// GuestPage.jsx's own drafts are, EXCEPT the password itself — see
// components/RegisterDetailsStep.jsx's own comment on why a credential
// never gets mirrored to localStorage even though every other field does.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import apiClient, { ApiError } from 'shared/api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import PortalShell from '../components/PortalShell.jsx';
import GuestEmailStep from '../components/GuestEmailStep.jsx';
import GuestVerifyStep from '../components/GuestVerifyStep.jsx';
import GuestDetailsStep from '../components/GuestDetailsStep.jsx';
import usePersistedState from '../hooks/usePersistedState.js';
import { mapApiErrorId } from '../utils/errorMapping.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';

const EMAIL_KEY = 'sneethel.portal.register.email';
const VERIFIED_AT_KEY = 'sneethel.portal.register.verifiedAt';
const DETAILS_KEY = 'sneethel.portal.register.details';

// Mirrors backend/src/services/registrationService.js's own
// OTP_RECEIPT_WINDOW_MINUTES (30) — same client-side-estimate-only stance
// as GuestPage.jsx's identical constant; the server is the real authority.
const RECEIPT_WINDOW_MS = 30 * 60 * 1000;

async function requestOtp(email) {
  return apiClient.post('/auth/register/otp/request', { email });
}

async function verifyOtp({ email, code }) {
  return apiClient.post('/auth/register/otp/verify', { email, code });
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail, clearEmail] = usePersistedState(EMAIL_KEY, '');
  const [verifiedAt, setVerifiedAt, clearVerifiedAt] = usePersistedState(VERIFIED_AT_KEY, null);
  const [details, setDetails, clearDetails] = usePersistedState(DETAILS_KEY, null);

  const isVerifiedFresh = Boolean(
    email && verifiedAt && Date.now() - verifiedAt < RECEIPT_WINDOW_MS
  );

  const [step, setStep] = useState(() => (isVerifiedFresh ? 'details' : 'email'));
  const [emailNoticeId, setEmailNoticeId] = useState(null);
  const [resendNoticeShown, setResendNoticeShown] = useState(false);

  const requestOtpMutation = useMutation({ mutationFn: requestOtp });
  const verifyOtpMutation = useMutation({ mutationFn: verifyOtp });
  const registerMutation = useMutation({ mutationFn: register });
  const { t, toastOk, toastErr } = useActionFeedback();

  const handleEmailSubmit = (submittedEmail) => {
    setEmailNoticeId(null);
    setEmail(submittedEmail);
    clearVerifiedAt();
    requestOtpMutation.mutate(submittedEmail, {
      onSuccess: () => {
        setStep('verify');
        toastOk(t('portal.guest.otpSentToast', { email: submittedEmail }));
      },
      onError: (err) => toastErr(err),
    });
  };

  const handleVerifySubmit = (code) => {
    verifyOtpMutation.mutate(
      { email, code },
      {
        onSuccess: () => {
          setVerifiedAt(Date.now());
          setStep('details');
          toastOk(t('portal.guest.otpVerifiedToast'));
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'OTP_TOO_MANY_ATTEMPTS') {
            setEmailNoticeId('error.OTP_TOO_MANY_ATTEMPTS');
            setStep('email');
          } else if (err instanceof ApiError && err.code === 'OTP_NOT_REQUESTED') {
            setEmailNoticeId('error.OTP_NOT_REQUESTED');
            setStep('email');
          }
          toastErr(err, ['OTP_INCORRECT', 'OTP_TOO_MANY_ATTEMPTS', 'OTP_NOT_REQUESTED']);
        },
      }
    );
  };

  const handleResend = () => {
    setResendNoticeShown(false);
    requestOtpMutation.mutate(email, {
      onSuccess: () => {
        setResendNoticeShown(true);
        toastOk(t('portal.guest.otpResentToast'));
      },
      onError: (err) => toastErr(err),
    });
  };

  const handleChangeEmail = () => {
    clearEmail();
    clearVerifiedAt();
    setStep('email');
  };

  const handleDetailsSubmit = (values) => {
    const { password, ...draftFields } = values;
    setDetails(draftFields);
    registerMutation.mutate(
      { ...draftFields, email, password },
      {
        onSuccess: () => {
          clearEmail();
          clearVerifiedAt();
          clearDetails();
          toastOk(t('portal.register.doneToast'));
          navigate('/account', { replace: true });
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'REGISTRATION_NOT_VERIFIED') {
            clearVerifiedAt();
            setEmailNoticeId('error.OTP_NOT_VERIFIED');
            setStep('email');
          }
          toastErr(err, ['DUPLICATE_EMAIL', 'VALIDATION_ERROR', 'REGISTRATION_NOT_VERIFIED']);
        },
      }
    );
  };

  const requestOtpErrorId = requestOtpMutation.error
    ? mapApiErrorId(requestOtpMutation.error, [])
    : null;
  const verifyErrorId = verifyOtpMutation.error
    ? mapApiErrorId(verifyOtpMutation.error, ['OTP_INCORRECT'])
    : null;

  return (
    <PortalShell backTo={step === 'email' ? '/login' : undefined} width="narrow">
      {step === 'email' && (
        <GuestEmailStep
          idPrefix="registerEmail"
          defaultEmail={email}
          onSubmit={handleEmailSubmit}
          isSubmitting={requestOtpMutation.isPending}
          noticeId={emailNoticeId}
          submitErrorId={requestOtpErrorId}
          submitError={requestOtpMutation.error}
        />
      )}
      {step === 'verify' && (
        <GuestVerifyStep
          idPrefix="registerVerify"
          email={email}
          onSubmit={handleVerifySubmit}
          isSubmitting={verifyOtpMutation.isPending}
          submitErrorId={verifyErrorId}
          submitError={verifyOtpMutation.error}
          onResend={handleResend}
          isResending={requestOtpMutation.isPending}
          resendErrorId={requestOtpErrorId}
          resendError={requestOtpMutation.error}
          resendNoticeShown={resendNoticeShown}
          onChangeEmail={handleChangeEmail}
        />
      )}
      {step === 'details' && (
        <GuestDetailsStep
          idPrefix="registerDetails"
          withPassword
          email={email}
          defaultValues={details}
          onDraftChange={setDetails}
          onContinue={handleDetailsSubmit}
          isSubmitting={registerMutation.isPending}
        />
      )}
    </PortalShell>
  );
}
